// MaintenanceScreen.js
import React, { useEffect, useRef, useState } from 'react';
import './AuthErrorScreen.css';
import './MaintenanceScreen.css';
import apiService from '../services/apiService';
import { clearMaintKey, isMaintenanceResponse, setMaintKey } from '../utils/maintenance';

/** 維護中輪詢間隔（毫秒）：維護結束後最多等這麼久就會自動重新載入。 */
const POLL_INTERVAL_MS = 30000;

/** 後端沒有提供維護訊息時使用的預設文字。 */
const DEFAULT_MESSAGE = '系統維護中，請稍後再試。';

/** 隱藏入口：在時間窗內連點圖示達到此次數，才會出現放行金鑰輸入列。 */
const SECRET_TAP_COUNT = 7;

/** 隱藏入口的連點時間窗（毫秒）。 */
const SECRET_TAP_WINDOW_MS = 5000;

/**
 * 系統維護中畫面（沿用 AuthErrorScreen 的版型樣式）。
 * 顯示期間每 30 秒詢問一次 /client/network，不再維護後自動重新載入頁面。
 * 內部測試者（例如 M+ WebView 沒有網址列、cookie 放行不通時）可連點圖示 7 次叫出輸入列，
 * 輸入放行金鑰後存入 localStorage，之後對 BFF 的請求都會帶 X-Maint-Key（見 utils/maintenance.js）。
 * 一般使用者看不到任何提示，畫面與一般維護頁無異。
 *
 * @param {{message?: string}} props message：後端提供的維護訊息
 */
function MaintenanceScreen({ message }) {
  /** 是否顯示放行金鑰輸入列（連點圖示後才為 true）。 */
  const [showKeyInput, setShowKeyInput] = useState(false);
  /** 輸入列的值。 */
  const [keyValue, setKeyValue] = useState('');
  /** 驗證失敗時短暫顯示的提示。 */
  const [keyError, setKeyError] = useState(false);
  /** 連點計數與起始時間（用 ref 避免每次點擊都重繪）。 */
  const tapRef = useRef({ count: 0, startedAt: 0 });

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const data = await apiService.request('/client/network');
        if (!isMaintenanceResponse(data)) {
          window.location.reload();
        }
      } catch (e) {
        // 查詢失敗（網路、閘道暫時異常）就維持畫面，下一輪再試
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  /** 圖示被點擊：時間窗內累積達門檻就顯示輸入列。 */
  const handleIconTap = () => {
    const now = Date.now();
    const tap = tapRef.current;
    if (now - tap.startedAt > SECRET_TAP_WINDOW_MS) {
      tap.count = 0;
      tap.startedAt = now;
    }
    tap.count += 1;
    if (tap.count >= SECRET_TAP_COUNT) {
      tap.count = 0;
      setShowKeyInput(true);
    }
  };

  /** 送出放行金鑰：先存起來再問一次 /client/network，通過就重新載入，失敗就清掉並提示。 */
  const handleSubmitKey = async (event) => {
    event.preventDefault();
    const key = keyValue.trim();
    if (!key) {
      return;
    }
    setMaintKey(key);
    try {
      const data = await apiService.request('/client/network');
      if (!isMaintenanceResponse(data)) {
        window.location.reload();
        return;
      }
    } catch (e) {
      // 視同驗證失敗
    }
    clearMaintKey();
    setKeyValue('');
    setKeyError(true);
    setTimeout(() => {
      setKeyError(false);
      setShowKeyInput(false);
    }, 1500);
  };

  return (
    <div className="auth-error-screen">
      <div className="auth-error-card">
        <div className="auth-error-icon" aria-hidden="true" onClick={handleIconTap}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M14.7 6.3a4 4 0 0 0 5 5L21 12.6 12.6 21 3 11.4l8.4-8.4 1.3 1.3a4 4 0 0 0 2 2Z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="auth-error-title">系統維護中</div>
        <div className="auth-error-message">{message || DEFAULT_MESSAGE}</div>
        <div className="auth-error-message">維護完成後，本頁面會自動重新載入。</div>
        <button type="button" className="auth-error-retry-btn" onClick={() => window.location.reload()}>
          重新整理
        </button>
        {showKeyInput && (
          <form className="maint-key-row" onSubmit={handleSubmitKey} autoComplete="off">
            <input
              className="maint-key-input"
              type="password"
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              placeholder={keyError ? '驗證失敗' : '驗證碼'}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
            />
            <button type="submit" className="maint-key-submit">
              確定
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default MaintenanceScreen;
